import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { auth, db } from "./firebase";
import { mapAnswersToProblems } from "./utils/assessmentUtils";

import Header from "./components/Header";
import SolutionDashboard from "./components/SolutionDashboard";
import AssessmentFlowPage from "./components/AssessmentFlowPage";
import PrivacyPolicy from "./components/PrivacyPolicy";
import DataDeletion from "./components/DataDeletion";
import WelcomeScreen from "./components/WelcomeScreen";
import AuthScreen from "./components/AuthScreen";
import QuickDestressTool from "./components/QuickDestressTool";
import RelaxationTool from "./components/RelaxationTool";
import VitaminDTracker from "./components/VitaminDTracker";
import AiConsentModal from "./components/AiConsentModal";

const BADGE_MILESTONES = [
  { days: 3, name: "Sunbeam Starter" },
  { days: 7, name: "Golden Glow" },
  { days: 14, name: "Radiant Ripple" },
  { days: 21, name: "Sunny Spirit" },
  { days: 30, name: "Sunshine Star" },
  { days: 60, name: "Solar Flare" },
  { days: 100, name: "Solar Guardian" },
];

function getLocalDateString(dateObj) {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function canRetakeAssessment(lockDate) {
  if (!lockDate) return false;
  const now = new Date();
  const diffDays = (now - new Date(lockDate)) / (1000 * 60 * 60 * 24);
  return diffDays >= 21;
}

// In-App Browser Detection Component (early warning)
function BrowserWarning() {
  useEffect(() => {
    if (/FBAN|FBAV|Instagram|Line|WebView/i.test(navigator.userAgent)) {
      alert(
        "Please open this site in Chrome, Safari, or your device's default browser. Google Sign-In will not work in in-app browsers."
      );
    }
  }, []);
  return null;
}

export default function App() {
  const navigate = useNavigate();

  // Auth and user profile state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [errorLoadingProfile, setErrorLoadingProfile] = useState(null);

  // Control showing the consent modal
  const [showConsentModal, setShowConsentModal] = useState(false);

  // Load or create user profile from Firestore
  const loadProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser?.uid) {
      setProfile(null);
      setErrorLoadingProfile(null);
      return;
    }
    setProfileLoading(true);
    setErrorLoadingProfile(null);

    const userRef = doc(db, "userProblems", firebaseUser.uid);
    try {
      const docSnap = await getDoc(userRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (!data.hasOwnProperty("hasCompleted")) {
          const completed =
            Array.isArray(data.lockedProblems) && data.lockedProblems.length > 0;
          await updateDoc(userRef, { hasCompleted: completed });
          data.hasCompleted = completed;
        }
        setProfile(data);
      } else {
        const newProfile = {
          email: firebaseUser.email || "",
          createdAt: new Date().toISOString(),
          hasCompleted: false,
          lockedProblems: [],
          badges: [],
          points: 0,
          streak: 0,
          skills: { stress: 0, anxiety: 0, sunlight: 0 },
          displayName: firebaseUser.displayName || "Anonymous",
          avatar: "🙂",
          dailyLogs: [],
        };
        await setDoc(userRef, newProfile);
        setProfile(newProfile);
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      setErrorLoadingProfile(error.message || "Unknown error");
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // Track auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
      if (firebaseUser) {
        await loadProfile(firebaseUser);
      } else {
        setProfile(null);
        setErrorLoadingProfile(null);
      }
    });
    return () => unsubscribe();
  }, [loadProfile]);

  // Defensive reload profile if needed
  useEffect(() => {
    if (user && !profile && !profileLoading && !errorLoadingProfile) {
      loadProfile(user);
    }
  }, [user, profile, profileLoading, errorLoadingProfile, loadProfile]);

  // Decide if consent is needed (only when profile exists)
  const hasCompleted = profile?.hasCompleted;
  const aiSet =
    profile && typeof profile.aiPersonalizationConsent === "boolean";
  const voiceSet =
    profile && typeof profile.voiceEmotionConsent === "boolean";
  const needsConsent = !!(user && hasCompleted && (!aiSet || !voiceSet));

  useEffect(() => {
    setShowConsentModal(needsConsent);
  }, [needsConsent]);

  // Helper to update user profile fields
  const updateProfileField = useCallback(
    async (field, value) => {
      if (!user) return;
      try {
        const userRef = doc(db, "userProblems", user.uid);
        await updateDoc(userRef, { [field]: value });
        setProfile((prev) => ({ ...prev, [field]: value }));
      } catch (err) {
        console.error(`Failed to update profile field '${field}':`, err);
      }
    },
    [user]
  );

  const handleSetDisplayName = (newName) => updateProfileField("displayName", newName);
  const handleSetAvatar = (newAvatar) => updateProfileField("avatar", newAvatar);

  // Handle daily log submission
  const handleDailyLogSubmit = useCallback(
    async (entry) => {
      if (!user) return;
      try {
        const userRef = doc(db, "userProblems", user.uid);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = getLocalDateString(today);

        // Update streak
        let newStreak = profile?.streak || 0;
        let lastCheckInDate = profile?.lastCheckIn ? new Date(profile.lastCheckIn) : null;
        if (lastCheckInDate) lastCheckInDate.setHours(0, 0, 0, 0);

        const diffDays = lastCheckInDate
          ? (today - lastCheckInDate) / (1000 * 60 * 60 * 24)
          : null;
        if (diffDays === 1) {
          newStreak += 1;
        } else if (!diffDays || diffDays > 1) {
          newStreak = 1;
        }

        // Update badges based on milestones
        let newBadges = profile?.badges ? [...profile.badges] : [];
        BADGE_MILESTONES.forEach(({ days, name }) => {
          if (newStreak === days && !newBadges.includes(name)) {
            newBadges.push(name);
            setProfile((prev) => ({ ...prev, newBadge: name }));
          }
        });

        // Update points
        let newPoints = (profile?.points || 0) + 10;
        // Update skills
        let newSkills = {
          ...(profile?.skills || { stress: 0, anxiety: 0, sunlight: 0 }),
        };
        if (entry?.type && entry.didPractice && newSkills.hasOwnProperty(entry.type)) {
          newSkills[entry.type] = (newSkills[entry.type] || 0) + 1;
        }

        // Update Firestore doc
        await updateDoc(userRef, {
          dailyLogs: arrayUnion({ ...entry, date: todayStr }),
          streak: newStreak,
          lastCheckIn: todayStr,
          badges: newBadges,
          points: newPoints,
          skills: newSkills,
        });

        // Update local state optimistically
        setProfile((prev) => ({
          ...prev,
          dailyLogs: [...(prev.dailyLogs || []), { ...entry, date: todayStr }],
          streak: newStreak,
          lastCheckIn: todayStr,
          badges: newBadges,
          points: newPoints,
          skills: newSkills,
        }));
      } catch (err) {
        console.error("Failed to save daily log entry:", err);
      }
    },
    [user, profile]
  );

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Sign out failed:", err);
    }
    setProfile(null);
    setUser(null);
    navigate("/"); // redirect after logout
  }, [navigate]);

  // Render loading screen during auth/profile loading
  if (authLoading || profileLoading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 20,
          marginTop: "40vh",
          fontFamily: "Arial, sans-serif",
          fontSize: "1.2rem",
        }}
      >
        <p>Loading your personalized experience...</p>
        <div style={{ fontSize: "2rem" }}>⏳</div>
      </div>
    );
  }

  // Render error screen with retry option
  if (errorLoadingProfile) {
    return (
      <div
        style={{
          textAlign: "center",
          color: "red",
          marginTop: "40vh",
          padding: 20,
          fontFamily: "Arial, sans-serif",
          fontSize: "1.2rem",
        }}
      >
        <h2>Error loading profile</h2>
        <p>{errorLoadingProfile}</p>
        <button
          onClick={() => loadProfile(user)}
          style={{
            padding: "10px 20px",
            fontSize: "1rem",
            cursor: "pointer",
            borderRadius: 5,
            border: "none",
            backgroundColor: "#f44336",
            color: "white",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <BrowserWarning />
      <Header user={user} setUser={setUser} />

      {showConsentModal && user && profile && (
        <AiConsentModal
          user={user}
          profile={profile}
          onClose={async () => {
            setShowConsentModal(false);
            await loadProfile(user);
          }}
        />
      )}

      <Routes>
        {/* Public / Unauthenticated routes */}
        {!user && (
          <>
            <Route
              path="/"
              element={
                <WelcomeScreen
                  onStartAssessment={() => {
                    console.log("Navigating to /assessment (unauthenticated)");
                    navigate("/assessment");
                  }}
                  onLogin={() => navigate("/login")}
                />
              }
            />
            <Route
              path="/assessment"
              element={
                <AssessmentFlowPage
                  user={null}
                  onComplete={(answers) => {
                    console.log("Anonymous assessment completed", answers);
                    navigate("/signup");
                  }}
                />
              }
            />
            <Route
              path="/login"
              element={<AuthScreen mode="login" onAuthSuccess={() => navigate("/")} />}
            />
            <Route
              path="/signup"
              element={<AuthScreen mode="signup" onAuthSuccess={() => navigate("/login")} />}
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route
              path="*"
              element={
                <WelcomeScreen
                  onStartAssessment={() => navigate("/assessment")}
                  onLogin={() => navigate("/login")}
                />
              }
            />
          </>
        )}

        {/* Authenticated but assessment not completed */}
        {user && !hasCompleted && (
          <>
            <Route
              path="/"
              element={
                <WelcomeScreen
                  onStartAssessment={() => navigate("/assessment")}
                  onLogin={() => navigate("/login")}
                />
              }
            />
            <Route
              path="/assessment"
              element={
                <AssessmentFlowPage
                  user={user}
                  onComplete={async (answers) => {
                    const mappedProblems = mapAnswersToProblems(answers);
                    try {
                      await setDoc(
                        doc(db, "userProblems", user.uid),
                        {
                          lockedProblems: mappedProblems,
                          lockDate: new Date().toISOString(),
                          dailyLogs: [],
                          points: 0,
                          streak: 0,
                          badges: [],
                          skills: { stress: 0, anxiety: 0, sunlight: 0 },
                          displayName: profile?.displayName || "Anonymous",
                          avatar: profile?.avatar || "🙂",
                          hasCompleted: true,
                        },
                        { merge: true }
                      );
                      await loadProfile(user);
                      navigate("/dashboard");
                    } catch (err) {
                      console.error("Failed to save assessment:", err);
                      alert("Failed to save your test, please try again.");
                    }
                  }}
                />
              }
            />
            <Route
              path="*"
              element={
                <WelcomeScreen
                  onStartAssessment={() => navigate("/assessment")}
                  onLogin={() => navigate("/login")}
                />
              }
            />
          </>
        )}

        {/* Authenticated and assessment completed */}
        {user && hasCompleted && (
          <>
            <Route
              path="/dashboard"
              element={
                <SolutionDashboard
                  user={user}
                  displayName={profile?.displayName || "Anonymous"}
                  avatar={profile?.avatar || "🙂"}
                  points={profile?.points || 0}
                  streak={profile?.streak || 0}
                  badges={profile?.badges || []}
                  skills={profile?.skills || { stress: 0, anxiety: 0, sunlight: 0 }}
                  problems={profile?.lockedProblems || []}
                  lockDate={profile?.lockDate || null}
                  canRetake={
                    profile?.lockDate ? canRetakeAssessment(profile.lockDate) : false
                  }
                  dailyLogs={profile?.dailyLogs || []}
                  onSubmit={handleDailyLogSubmit}
                  onLogout={handleLogout}
                  onSetDisplayName={handleSetDisplayName}
                  onSetAvatar={handleSetAvatar}
                  newBadge={profile?.newBadge || null}
                  onClosePopup={() => setProfile((p) => ({ ...p, newBadge: null }))}
                  aiPersonalizationConsent={profile?.aiPersonalizationConsent === true}
                  voiceEmotionConsent={profile?.voiceEmotionConsent === true}
                />
              }
            />
            <Route path="/quick-destress" element={<QuickDestressTool user={user} />} />
            <Route
              path="/progressive-relaxation"
              element={<RelaxationTool user={user} />}
            />
            <Route path="/vitamin-d-tracker" element={<VitaminDTracker user={user} />} />

            <Route
              path="*"
              element={
                <SolutionDashboard
                  user={user}
                  displayName={profile?.displayName || "Anonymous"}
                  avatar={profile?.avatar || "🙂"}
                  points={profile?.points || 0}
                  streak={profile?.streak || 0}
                  badges={profile?.badges || []}
                  skills={profile?.skills || { stress: 0, anxiety: 0, sunlight: 0 }}
                  problems={profile?.lockedProblems || []}
                  lockDate={profile?.lockDate || null}
                  canRetake={
                    profile?.lockDate ? canRetakeAssessment(profile.lockDate) : false
                  }
                  dailyLogs={profile?.dailyLogs || []}
                  onSubmit={handleDailyLogSubmit}
                  onLogout={handleLogout}
                  onSetDisplayName={handleSetDisplayName}
                  onSetAvatar={handleSetAvatar}
                  newBadge={profile?.newBadge || null}
                  onClosePopup={() => setProfile((p) => ({ ...p, newBadge: null }))}
                  aiPersonalizationConsent={profile?.aiPersonalizationConsent === true}
                  voiceEmotionConsent={profile?.voiceEmotionConsent === true}
                />
              }
            />
          </>
        )}
      </Routes>
    </>
  );
}
